var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { Segmented } from "antd";
import { CompsChart, COMPARISON_REFERENCE } from "@ms/shared-components";
import { ORDER_TRAFFIC_CHART_SETTINGS } from "./config/index";
import { AXIS_LABEL_INTEGER } from "../../configs/tooltipCommon";
import { EmptyDataPlaceholder } from "../EmptyDataPlaceholder";
import { pickOrderTrafficChart, } from "../../hooks/useOrderTrafficData";
/** 订单流速无数据时的空 combo 配置 */
var ORDER_TRAFFIC_EMPTY_COMBO_CONFIG = {
    categories: [],
    dataset: {},
    axes: {
        left: {
            id: "left",
            position: "left",
            name: "订单量",
            unit: "单",
            min: 0,
            max: 100,
            axisLabel: AXIS_LABEL_INTEGER,
        },
    },
    series: [],
};
function ensureChartSegment(segment) {
    var _a;
    if (!segment || typeof segment !== "object")
        return null;
    var inner = "chartData" in segment && typeof segment.chartData === "object"
        ? segment.chartData
        : segment;
    if (!inner || !Array.isArray(inner.series) || inner.series.length === 0)
        return null;
    return __assign(__assign({}, inner), { categories: Array.isArray(inner.categories) ? inner.categories : [], dataset: inner.dataset && typeof inner.dataset === "object" ? inner.dataset : {}, series: inner.series, axes: (_a = inner.axes) !== null && _a !== void 0 ? _a : {}, comparison: inner.comparison });
}
function mapInitialGranularity(initial) {
    if (initial === "天")
        return "7d";
    return "24h";
}
var OrderTraffic = function (_a) {
    var style = _a.style, storeId = _a.storeId, bundle = _a.bundle, _b = _a.loading, loading = _b === void 0 ? false : _b, onTitleClick = _a.onTitleClick, initialGranularity = _a.initialGranularity, titleLeftAdornment = _a.titleLeftAdornment, _c = _a.embedInCard, embedInCard = _c === void 0 ? false : _c;
    var _d = useState(function () {
        return mapInitialGranularity(initialGranularity);
    }), range = _d[0], setRange = _d[1];
    React.useEffect(function () {
        if (initialGranularity !== undefined) {
            setRange(mapInitialGranularity(initialGranularity));
        }
    }, [initialGranularity]);
    var baseChartData = useMemo(function () { return ensureChartSegment(pickOrderTrafficChart(bundle, range)); }, [bundle, range]);
    var orderTrafficChartData = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (!(baseChartData === null || baseChartData === void 0 ? void 0 : baseChartData.series) || !Array.isArray(baseChartData.series)) {
            return ORDER_TRAFFIC_EMPTY_COMBO_CONFIG;
        }
        var chartData = __assign(__assign({}, baseChartData), { series: baseChartData.series.map(function (s) { return (__assign(__assign({}, s), { stack: s.stack || s.stackId, showInLegend: typeof s.showInLegend === "boolean" ? s.showInLegend : false })); }) });
        if (range === "24h" &&
            Array.isArray(chartData.categories) &&
            chartData.categories.length > 0) {
            chartData.categories = __spreadArray([], chartData.categories, true).reverse();
            var ds = ((_a = chartData.dataset) !== null && _a !== void 0 ? _a : {});
            chartData.dataset = {};
            for (var _i = 0, _o = Object.keys(ds); _i < _o.length; _i++) {
                var key = _o[_i];
                var arr = ds[key];
                chartData.dataset[key] = Array.isArray(arr)
                    ? __spreadArray([], arr, true).reverse()
                    : arr;
            }
            chartData.axes = __assign(__assign({}, ((_b = chartData.axes) !== null && _b !== void 0 ? _b : {})), { bottom: __assign(__assign({}, ((_d = ((_c = chartData.axes) === null || _c === void 0 ? void 0 : _c.bottom)) !== null && _d !== void 0 ? _d : {})), { inverse: true }) });
        }
        var dataset = ((_e = chartData.dataset) !== null && _e !== void 0 ? _e : {});
        var categories = ((_f = chartData.categories) !== null && _f !== void 0 ? _f : []);
        var barSeries = chartData.series.filter(function (s) { return (s.axisId === "left" || !s.axisId) && s.type === "bar"; });
        var n = categories.length;
        var maxBarValue = 0;
        var isStacked = barSeries.some(function (s) { return s.stack || s.stackId; });
        if (barSeries.length > 0 && n > 0) {
            if (isStacked) {
                for (var _p = 0, _q = Array.from({ length: n }, function (_, j) { return j; }); _p < _q.length; _p++) {
                    var i = _q[_p];
                    var sum = 0;
                    for (var _r = 0, barSeries_1 = barSeries; _r < barSeries_1.length; _r++) {
                        var s = barSeries_1[_r];
                        var arr = dataset[s.dataKey];
                        if (Array.isArray(arr) && typeof arr[i] === "number")
                            sum += arr[i];
                    }
                    maxBarValue = Math.max(maxBarValue, sum);
                }
            }
            else {
                for (var _s = 0, barSeries_2 = barSeries; _s < barSeries_2.length; _s++) {
                    var s = barSeries_2[_s];
                    var arr = dataset[s.dataKey];
                    if (!Array.isArray(arr))
                        continue;
                    for (var _t = 0, arr_1 = arr; _t < arr_1.length; _t++) {
                        var v = arr_1[_t];
                        if (typeof v === "number")
                            maxBarValue = Math.max(maxBarValue, v);
                    }
                }
            }
        }
        var leftAxisMax = Math.max(1, maxBarValue * 2);
        var axisLabelInteger = AXIS_LABEL_INTEGER;
        var axes = ((_g = chartData.axes) !== null && _g !== void 0 ? _g : {});
        chartData.axes = __assign(__assign(__assign({}, axes), { left: __assign(__assign({}, ((_h = axes.left) !== null && _h !== void 0 ? _h : {})), { id: "left", position: "left", name: (_k = (_j = axes.left) === null || _j === void 0 ? void 0 : _j.name) !== null && _k !== void 0 ? _k : "订单量", unit: (_m = (_l = axes.left) === null || _l === void 0 ? void 0 : _l.unit) !== null && _m !== void 0 ? _m : "单", max: leftAxisMax, axisLabel: axisLabelInteger }) }), (axes.right
            ? {
                right: __assign(__assign({}, axes.right), { axisLabel: axisLabelInteger, axisLine: { show: true, lineStyle: { color: "#73c0de", width: 1 } }, axisTick: {
                        show: true,
                        length: 4,
                        inside: true,
                        lineStyle: { color: "#73c0de", width: 1 },
                    } }),
            }
            : {}));
        chartData.series = chartData.series.filter(function (s) {
            return (s === null || s === void 0 ? void 0 : s.id) !== "order-traffic-comparison-yoy" &&
                (s === null || s === void 0 ? void 0 : s.id) !== "order-traffic-comparison-wow";
        });
        delete chartData.markLines;
        if ((baseChartData === null || baseChartData === void 0 ? void 0 : baseChartData.comparison) && categories.length > 0) {
            var cmp = baseChartData.comparison;
            var yoyVal_1 = typeof cmp.yearOverYear === "number" ? cmp.yearOverYear : Number(cmp.yearOverYear);
            var wowVal_1 = typeof cmp.weekOverWeek === "number" ? cmp.weekOverWeek : Number(cmp.weekOverWeek);
            var flatYoy = "__orderTrafficComparisonYoy";
            var flatWow = "__orderTrafficComparisonWow";
            var nextDataset = __assign({}, chartData.dataset);
            if (Number.isFinite(yoyVal_1)) {
                nextDataset[flatYoy] = categories.map(function () { return yoyVal_1; });
                chartData.series.push({
                    id: "order-traffic-comparison-yoy",
                    name: COMPARISON_REFERENCE.yearOverYear.label,
                    dataKey: flatYoy,
                    type: "line",
                    axisId: "right",
                    color: COMPARISON_REFERENCE.yearOverYear.color,
                    lineStyle: __assign({}, COMPARISON_REFERENCE.yearOverYear.lineStyle),
                    showSymbol: false,
                    smooth: false,
                    showInLegend: true,
                    legendHoverLink: false,
                    silent: true,
                    z: 10,
                });
            }
            if (Number.isFinite(wowVal_1)) {
                nextDataset[flatWow] = categories.map(function () { return wowVal_1; });
                chartData.series.push({
                    id: "order-traffic-comparison-wow",
                    name: COMPARISON_REFERENCE.weekOverWeek.label,
                    dataKey: flatWow,
                    type: "line",
                    axisId: "right",
                    color: COMPARISON_REFERENCE.weekOverWeek.color,
                    lineStyle: __assign({}, COMPARISON_REFERENCE.weekOverWeek.lineStyle),
                    showSymbol: false,
                    smooth: false,
                    showInLegend: true,
                    legendHoverLink: false,
                    silent: true,
                    z: 10,
                });
            }
            chartData.dataset = nextDataset;
        }
        return chartData;
    }, [baseChartData, range]);
    var hasValidData = orderTrafficChartData &&
        Array.isArray(orderTrafficChartData.series) &&
        orderTrafficChartData.series.length > 0 &&
        Array.isArray(orderTrafficChartData.categories) &&
        orderTrafficChartData.categories.length > 0;
    var shellStyle = embedInCard
        ? __assign({ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0, boxSizing: "border-box" }, style) : __assign({ display: "flex", flexDirection: "column", width: 400, height: 300, border: "1px solid #f0f0f0", borderRadius: 4, background: "#fafafa", boxSizing: "border-box", overflow: "visible", flexShrink: 0 }, style);
    return (_jsxs("div", __assign({ "data-component-id": "chart:order-traffic", style: shellStyle }, { children: [_jsxs("div", __assign({ style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: "1px solid #f0f0f0",
                    background: "#fff",
                    flexShrink: 0,
                } }, { children: [_jsxs("div", __assign({ style: { display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 } }, { children: [titleLeftAdornment && (_jsx("div", __assign({ style: {
                                    marginLeft: -4,
                                    marginRight: 4,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                } }, { children: titleLeftAdornment }))), _jsx("div", __assign({ onClick: onTitleClick, style: {
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#333",
                                    cursor: onTitleClick ? "pointer" : "default",
                                    userSelect: "none",
                                    transition: "color 0.3s ease",
                                }, onMouseEnter: function (e) {
                                    if (onTitleClick)
                                        e.currentTarget.style.color = "#1890ff";
                                }, onMouseLeave: function (e) {
                                    if (onTitleClick)
                                        e.currentTarget.style.color = "#333";
                                }, title: onTitleClick ? "点击查看订单流速分析" : undefined }, { children: "\u8BA2\u5355\u6D41\u901F" }))] })), _jsx(Segmented, { size: "small", value: range, onChange: function (v) { return setRange(v); }, options: [
                            { label: "24小时", value: "24h" },
                            { label: "7天", value: "7d" },
                        ] })] })), _jsx("div", __assign({ style: {
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    padding: 0,
                    boxSizing: "border-box",
                    overflow: "visible",
                    position: "relative",
                    display: "flex",
                    height: "100%",
                    background: "#fff",
                } }, { children: loading ? (_jsx(EmptyDataPlaceholder, { description: "\u52A0\u8F7D\u4E2D\u2026" })) : hasValidData ? (_jsx(CompsChart, { type: "combo", data: orderTrafficChartData, settings: ORDER_TRAFFIC_CHART_SETTINGS, style: { width: "100%", height: "100%" } }, "orderTraffic-".concat(range, "-").concat(storeId))) : (_jsx(EmptyDataPlaceholder, { description: "\u6682\u65E0\u6570\u636E" })) }))] })));
};
export default OrderTraffic;
